import { Button, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger } from '@fluentui/react-components'
import { Navigation24Regular } from '@fluentui/react-icons'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { useGetSearchParam, useSessionStorage } from '@/hooks'
import { ConfirmDialog } from '@/ui/Dialog/ConfirmDialog'
import { OverlayDrawerComponent } from '@/ui/OverlayDrawerComponent/OverlayDrawerComponent'
import { MessagesStorageStorageFileType } from '@/util/enums'
import { isPlatformAdmin, isProjectAdmin, isProjectLeader, isSuperUser } from '@/util/roles'
import { EntityFilesList } from '../../EntityFilesList'

type MessagesWorkspacesEntity = RestSchemas['Messages.Workspaces.Entity']

const isDeletePermitted = (userRole: number) =>
  isProjectAdmin(userRole) || isProjectLeader(userRole) || isPlatformAdmin(userRole) || isSuperUser(userRole)

export const RowActionsMenu = ({ entity }: { entity: MessagesWorkspacesEntity }) => {
  const queryClient = useQueryClient()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const workspaceId = useGetSearchParam<number>('subscriptionId')
  const [userRole] = useSessionStorage('roles', 1)
  const [drawerOpen, setDrawerOpen] = useState<'none' | 'templates' | 'dataAssets'>('none')

  const restClient = createRestClient()
  //console.log('REST Client:', restClient)

  if (!restClient?.deleteWorkspacesApiDeleteEntity) {
    //console.error('❌ Delete API function is missing from Rest Client!')
    return null // Prevents crashing if API is missing
  }

  const { deleteWorkspacesApiDeleteEntity, getWorkspacesApiGetOrganisationWorkspaceEntities } = restClient

  //console.log('✅ deleteWorkspacesApiDeleteEntity:', deleteWorkspacesApiDeleteEntity)

  const entityId = Number(entity.entityId) // Ensure it's a number

  const { mutate: deleteEntity } = deleteWorkspacesApiDeleteEntity.useMutation(
    { query: { entityId } }, // Only send a simple number
    {
      onSuccess: () => {
        //console.log('✅ Delete successful!')
        getWorkspacesApiGetOrganisationWorkspaceEntities.invalidateQueries(
          { parameters: { query: { workspaceId } } },
          queryClient
        )
      },
      onError: (error) => {
        console.error('❌ Delete failed:', error)
        toast.error('Failed to delete entity')
      },
    }
  )

  return (
    <>
      <Menu
        open={isMenuOpen}
        onOpenChange={(_e, { open }) => {
          setIsMenuOpen(open)
        }}
      >
        <MenuTrigger disableButtonEnhancement>
          <Button icon={<Navigation24Regular />} />
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            {isDeletePermitted(userRole) && (
              <MenuItem persistOnClick>
                <ConfirmDialog
                  trigger={<div>Delete entity</div>}
                  title="Delete entity"
                  content="Please confirm that the entity should be removed from the workspace?"
                  onConfirm={() => deleteEntity()} // ✅ FIXED
                  onClose={() => setIsMenuOpen(false)}
                />
              </MenuItem>
            )}
            <MenuItem onClick={() => setDrawerOpen('dataAssets')}>Manage Templates</MenuItem>
            <MenuItem onClick={() => setDrawerOpen('dataAssets')}>Manage Data Assets</MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>

      {workspaceId && (
        <>
          <OverlayDrawerComponent
            open={drawerOpen === 'templates'}
            setOpen={(open) => setDrawerOpen(open ? 'templates' : 'none')}
            size="medium"
            title="Manage Entity Template Uploads"
            content={
              <EntityFilesList
                onSubmit={() => setDrawerOpen('none')}
                workspaceId={workspaceId}
                selectedEntity={entity}
                fileType={MessagesStorageStorageFileType.Template}
              />
            }
          />
          <OverlayDrawerComponent
            open={drawerOpen === 'dataAssets'}
            setOpen={(open) => setDrawerOpen(open ? 'dataAssets' : 'none')}
            size="medium"
            title="Manage Entity Data Assets"
            content={
              <EntityFilesList
                onSubmit={() => setDrawerOpen('none')}
                workspaceId={workspaceId}
                selectedEntity={entity}
                fileType={MessagesStorageStorageFileType.Result}
              />
            }
          />
        </>
      )}
    </>
  )
}
